/**
 * useEnv Hook - Application Layer
 *
 * React hook for accessing environment configuration in components.
 * Wraps the envService infrastructure layer for React component usage.
 *
 * Architecture:
 * Components → useEnv (hook) → envService (infrastructure)
 *
 * Usage:
 *   import { useEnv } from '../hooks/useEnv';
 *
 *   const MyComponent = () => {
 *     const { logoUrl, apiUrl, isNewUI } = useEnv();
 *     return <img src={logoUrl} alt="Logo" />;
 *   };
 */

import { useMemo } from 'react';
import { envService } from '../services/env.service';
import type { EnvironmentConfig } from '../types/env';

/**
 * Hook return type - provides the complete environment configuration
 * plus convenience methods
 */
export interface UseEnvReturn extends EnvironmentConfig {
  // Convenience methods
  isFeatureEnabled: (feature: keyof EnvironmentConfig) => boolean;
  getUIConfig: () => EnvironmentConfig['ui_config'];
  getTermsAndConditions: () => EnvironmentConfig['termsAndConditions'];
  getAPMStatus: () => EnvironmentConfig['apmStatus'];
  reload: () => void;
}

/**
 * React hook for accessing environment configuration
 *
 * Returns the complete environment configuration object with type safety.
 * Memoized to prevent unnecessary re-renders.
 *
 * @returns {UseEnvReturn} Environment configuration with convenience methods
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { logoUrl, apiUrl, pageSize } = useEnv();
 *
 *   return (
 *     <div>
 *       <img src={logoUrl} alt="Logo" />
 *       <p>API: {apiUrl}</p>
 *       <p>Page Size: {pageSize}</p>
 *     </div>
 *   );
 * };
 * ```
 *
 * @example
 * ```tsx
 * const FeatureComponent = () => {
 *   const env = useEnv();
 *
 *   if (env.isFeatureEnabled('faqUrl')) {
 *     return <FAQButton />;
 *   }
 *
 *   return null;
 * };
 * ```
 */
export function useEnv(): UseEnvReturn {
  // Get configuration from service
  // useMemo ensures we don't create new objects on every render
  const config = useMemo(() => {
    const envConfig = envService.getConfig();

    return {
      ...envConfig,
      // Bind methods to preserve 'this' context
      isFeatureEnabled: envService.isFeatureEnabled.bind(envService),
      getUIConfig: envService.getUIConfig.bind(envService),
      getTermsAndConditions: envService.getTermsAndConditions.bind(envService),
      getAPMStatus: envService.getAPMStatus.bind(envService),
      reload: envService.reload.bind(envService),
    };
  }, []); // Empty dependency array - env config is static after initial load

  return config;
}

/**
 * Convenience hook for getting just the logo URL
 * Useful when you only need the logo
 *
 * @returns {string} Logo URL
 *
 * @example
 * ```tsx
 * const Header = () => {
 *   const logoUrl = useLogoUrl();
 *   return <img src={logoUrl} alt="Logo" />;
 * };
 * ```
 */
export function useLogoUrl(): string {
  return useMemo(() => envService.logoUrl, []);
}

/**
 * Convenience hook for getting just the API URL
 * Useful when you only need the API URL
 *
 * @returns {string} API URL
 *
 * @example
 * ```tsx
 * const ApiStatus = () => {
 *   const apiUrl = useApiUrl();
 *   return <p>Connected to: {apiUrl}</p>;
 * };
 * ```
 */
export function useApiUrl(): string {
  return useMemo(() => envService.apiUrl, []);
}

/**
 * Convenience hook for checking feature flags
 *
 * @param {keyof EnvironmentConfig} feature - Feature name to check
 * @returns {boolean} Whether the feature is enabled
 *
 * @example
 * ```tsx
 * const FAQSection = () => {
 *   const isFaqEnabled = useFeatureFlag('faqUrl');
 *
 *   if (!isFaqEnabled) return null;
 *
 *   return <FAQButton />;
 * };
 * ```
 */
export function useFeatureFlag(feature: keyof EnvironmentConfig): boolean {
  return useMemo(() => envService.isFeatureEnabled(feature), [feature]);
}

/**
 * Re-export types for convenience
 */
export type { EnvironmentConfig } from '../types/env';
